import { useMemo, useState } from 'react';
import { Plus, ChevronLeft, ChevronRight, Trash2, Check, X, Users, User, Globe, ClipboardCheck } from 'lucide-react';
import type { AudienceType, WorkoutAssignment } from '@techsofit/api-types';
import { Button, Card, Modal, Skeleton, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { ScheduleForm } from '../components/ScheduleForm';
import { useAsync } from '../lib/useAsync';
import {
  getBoard,
  deleteAssignment,
  getAssignmentCompletions,
  markAssignmentCompletion,
} from '../lib/gymApi';

function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function mondayOf(d: Date): Date {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0 = Monday
  return addDays(x, -dow);
}

const DAY_FMT = new Intl.DateTimeFormat('en', { weekday: 'short', day: 'numeric', month: 'short' });

/** The board — week view of scheduled workouts; schedule new ones. */
export function WorkoutSchedulePage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [forDate, setForDate] = useState<string | null>(null);
  const [whoFor, setWhoFor] = useState<WorkoutAssignment | null>(null);
  const confirm = useConfirm();

  const from = isoDate(weekStart);
  const to = isoDate(addDays(weekStart, 6));
  const { data, loading, reload } = useAsync(() => getBoard(from, to), [from, to]);

  const byDay = useMemo(() => {
    const map = new Map<string, WorkoutAssignment[]>();
    for (const a of data ?? []) {
      (map.get(a.scheduledDate) ?? map.set(a.scheduledDate, []).get(a.scheduledDate)!).push(a);
    }
    return map;
  }, [data]);

  const todayIso = isoDate(new Date());
  const isCurrentWeek = from === isoDate(mondayOf(new Date()));
  // Hide past days by default; the previous-week button is how you "request" them.
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).filter(
    (d) => !isCurrentWeek || isoDate(d) >= todayIso,
  );

  async function removeAssignment(a: WorkoutAssignment) {
    const ok = await confirm({ title: 'Remove from the board?', description: `${a.workoutName} for ${a.audienceName}.`, confirmLabel: 'Remove', tone: 'danger' });
    if (!ok) return;
    await deleteAssignment(a.id);
    reload();
  }

  return (
    <section>
      <PageHeader
        title="Schedule"
        description="Place workouts on the board — for a member, a group, or everyone."
        actions={
          <Button onClick={() => setForDate(todayIso)}>
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">Schedule</span>
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
          <ChevronLeft className="size-4" aria-hidden />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))}>
          This week
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
          <ChevronRight className="size-4" aria-hidden />
        </Button>
        <span className="ml-1 text-sm text-muted">
          {DAY_FMT.format(weekStart)} – {DAY_FMT.format(addDays(weekStart, 6))}
        </span>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-card" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {days.map((d) => {
            const iso = isoDate(d);
            const list = byDay.get(iso) ?? [];
            const isToday = iso === todayIso;
            return (
              <Card key={iso} className={cn('flex flex-col p-4', isToday && 'ring-2 ring-accent')}>
                <div className="mb-2 flex items-center justify-between">
                  <p className={cn('text-sm font-semibold', isToday ? 'text-ink' : 'text-ink-soft')}>
                    {DAY_FMT.format(d)} {isToday && <span className="text-accent-ink">· Today</span>}
                  </p>
                  <button type="button" aria-label="Add to this day" onClick={() => setForDate(iso)} className="text-muted hover:text-ink">
                    <Plus className="size-4" aria-hidden />
                  </button>
                </div>
                {list.length === 0 ? (
                  <p className="py-2 text-sm text-muted">—</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {list.map((a) => (
                      <li key={a.id} className="flex items-start gap-2 rounded-control bg-surface p-2.5">
                        <AudienceIcon type={a.audienceType} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink">{a.workoutName}</p>
                          <p className="truncate text-xs text-muted">
                            {a.audienceName}
                            {a.timeLabel ? ` · ${a.timeLabel}` : ''}
                            {a.openSession ? ' · open' : ''}
                          </p>
                        </div>
                        <button type="button" aria-label="Who did it" onClick={() => setWhoFor(a)} className="text-muted hover:text-ink">
                          <ClipboardCheck className="size-4" aria-hidden />
                        </button>
                        <button type="button" aria-label="Remove" onClick={() => removeAssignment(a)} className="text-muted hover:text-bad-ink">
                          <Trash2 className="size-4" aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {forDate && (
        <ScheduleForm
          date={forDate}
          onClose={() => setForDate(null)}
          onSaved={() => {
            setForDate(null);
            reload();
          }}
        />
      )}

      {whoFor && <CompletionsModal assignment={whoFor} onClose={() => setWhoFor(null)} />}
    </section>
  );
}

function CompletionsModal({ assignment, onClose }: { assignment: WorkoutAssignment; onClose: () => void }) {
  const { data, loading } = useAsync(() => getAssignmentCompletions(assignment.id), [assignment.id]);
  const [done, setDone] = useState<Record<string, boolean>>({});

  // Local override on top of the loaded data (optimistic ticks).
  const rows = (data ?? []).map((r) => ({ ...r, completed: r.memberId in done ? done[r.memberId] : r.completed }));

  function toggle(memberId: string, next: boolean) {
    setDone((p) => ({ ...p, [memberId]: next }));
    markAssignmentCompletion(assignment.id, memberId, next).catch(() => setDone((p) => ({ ...p, [memberId]: !next })));
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[80vh] w-full flex-col">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Who did it</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <p className="mt-1 text-sm text-muted">
          {assignment.workoutName} · {assignment.audienceName}
        </p>

        <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <p className="py-8 text-center text-sm text-muted">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">
              {assignment.audienceType === 'EVERYONE'
                ? "No one's ticked this yet. Members who mark it done will show here."
                : 'No members to show.'}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {rows.map((r) => (
                <li key={r.memberId}>
                  <button type="button" onClick={() => toggle(r.memberId, !r.completed)} className="flex w-full items-center gap-3 py-2.5 text-left">
                    <span className={cn('flex size-6 items-center justify-center rounded-full border', r.completed ? 'border-ok-ink bg-ok-ink text-white' : 'border-line-strong text-transparent')}>
                      <Check className="size-3.5" aria-hidden />
                    </span>
                    <span className={cn('text-sm', r.completed ? 'text-ink' : 'text-ink-soft')}>{r.memberName}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-3 flex justify-end border-t border-line pt-3">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function AudienceIcon({ type }: { type: AudienceType }) {
  const Icon = type === 'EVERYONE' ? Globe : type === 'GROUP' ? Users : User;
  return (
    <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-control bg-elevated text-ink-soft">
      <Icon className="size-4" aria-hidden />
    </span>
  );
}
